/**
 * GMB Opening Hours Type Definitions
 * Based on Google My Business API structure
 */

/**
 * @typedef {Object} TimeOfDay
 * @property {number} hours - Hour of day (0-23)
 * @property {number} [minutes] - Minutes (0-59), defaults to 0
 */

/**
 * @typedef {'MONDAY'|'TUESDAY'|'WEDNESDAY'|'THURSDAY'|'FRIDAY'|'SATURDAY'|'SUNDAY'} DayName
 */

/**
 * @typedef {Object} Period
 * @property {DayName} openDay - Day the period starts
 * @property {TimeOfDay} openTime - Opening time
 * @property {DayName} closeDay - Day the period ends
 * @property {TimeOfDay} closeTime - Closing time
 */

/**
 * @typedef {Object} RegularHours
 * @property {Period[]} periods - Array of opening periods
 */

/**
 * @typedef {Object} DateObject
 * @property {number} year
 * @property {number} month - 1-12
 * @property {number} day - 1-31
 */

/**
 * @typedef {Object} SpecialHourPeriod
 * @property {DateObject} startDate - The date this special period applies to
 * @property {TimeOfDay} [openTime] - Opening time (if not closed)
 * @property {TimeOfDay} [closeTime] - Closing time (if not closed)
 * @property {boolean} [closed] - True if closed for the entire day
 */

/**
 * @typedef {Object} SpecialHours
 * @property {SpecialHourPeriod[]} specialHourPeriods - Array of special hour periods
 */

/**
 * @typedef {Object} LocationHours
 * @property {RegularHours} [regularHours]
 * @property {SpecialHours} [specialHours]
 */

/**
 * @typedef {Object} GMBLocation
 * @property {string} name - Format: 'locations/{locationId}'
 * @property {RegularHours} [regularHours]
 * @property {SpecialHours} [specialHours]
 */

/**
 * @typedef {Object} OpeningHoursData
 * @property {GMBLocation[]} locations
 */

/**
 * @typedef {'closed'|'open'|'modified'} EffectiveHoursType
 */

/**
 * @typedef {Object} EffectiveHours
 * @property {EffectiveHoursType} type - Whether store is open, closed, or has modified hours
 * @property {Period[]} [periods] - Opening periods for this day
 * @property {boolean} isSpecialDay - True if this day has special hours
 * @property {string} [reason] - Optional reason for special hours
 */

/**
 * @typedef {Object} GroupedSpecialHours
 * @property {DateObject} startDate
 * @property {DateObject} endDate
 * @property {boolean} closed
 * @property {TimeOfDay} [openTime]
 * @property {TimeOfDay} [closeTime]
 */

/**
 * Unavailability reasons for slots
 * @typedef {'STORE_CLOSED_REGULAR'|'STORE_CLOSED_SPECIAL'|'CAPACITY_FULL'|'TOO_SOON'|null} UnavailabilityReason
 */

/**
 * @typedef {Object} EnhancedSlot
 * @property {string} timestamp
 * @property {string} time_caption
 * @property {number} availability
 * @property {number} price
 * @property {string} price_currency
 * @property {string} [comment]
 * @property {UnavailabilityReason} [unavailability_reason]
 * @property {boolean} [is_special_hours]
 */

// Day order for iteration (Monday = 1, Sunday = 7 in ISO)
export const DAYS_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Map ISO day number to day name
export const ISO_DAY_TO_NAME = {
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
  7: 'SUNDAY'
};

// Map day name to ISO day number
export const DAY_NAME_TO_ISO = {
  'MONDAY': 1,
  'TUESDAY': 2,
  'WEDNESDAY': 3,
  'THURSDAY': 4,
  'FRIDAY': 5,
  'SATURDAY': 6,
  'SUNDAY': 7
};